import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
    const { user, login } = useAuth();
    const [mode, setMode] = useState("login"); // login | register
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    if (user) {
        return <Navigate to={user.role === "admin" ? "/admin" : "/me"} replace />;
    }

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        if (mode === "login") {
            const res = await login(email, password);
            setLoading(false);
            if (res.ok) {
                toast.success("Sesión iniciada");
            } else {
                toast.error(res.error);
            }
        } else {
            try {
                await api.post("/auth/register", { email, password, name });
                toast.success("Cuenta creada");
                window.location.href = "/me";
            } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al registrarse");
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <div className="min-h-screen grid md:grid-cols-2">
            <div className="hidden md:block bg-foreground text-background p-12 relative">
                <Logo variant="seal-light" size={140} />
                <div className="overline text-background/60 mt-12">Mi cuenta</div>
                <h1 className="font-display text-6xl leading-none mt-6 tracking-tighter">
                    Tu boda,<br/>tu archivo.
                </h1>
                <p className="text-sm opacity-70 mt-8 max-w-md">
                    Accede a tus cotizaciones guardadas, gestiona tu reserva y comunícate con nuestro equipo en un solo lugar.
                </p>
                <div className="absolute bottom-12 left-12 right-12 text-xs opacity-60">
                    ¿Eres administración? Usa el <Link to="/admin/login" className="underline-editorial">acceso de admin</Link>.
                </div>
            </div>
            <div className="flex items-center justify-center p-8 md:p-16">
                <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="customer-auth-form">
                    <div>
                        <div className="overline text-muted-foreground">Hacienda Nápoles</div>
                        <h2 className="font-display text-4xl mt-2 tracking-tighter">
                            {mode === "login" ? "Inicia sesión" : "Crea tu cuenta"}
                        </h2>
                    </div>

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setMode("login")}
                            className={`px-3 py-1.5 overline border ${mode === "login" ? "bg-foreground text-background border-foreground" : "border-border"}`}
                            data-testid="tab-login"
                        >Ingresar</button>
                        <button
                            type="button"
                            onClick={() => setMode("register")}
                            className={`px-3 py-1.5 overline border ${mode === "register" ? "bg-foreground text-background border-foreground" : "border-border"}`}
                            data-testid="tab-register"
                        >Registrarme</button>
                    </div>

                    {mode === "register" && (
                        <div className="flex flex-col gap-2">
                            <Input
                                data-testid="reg-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="María Fernanda Salazar"
                                className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-11"
                            />
                            <Label className="overline text-muted-foreground">Nombre completo</Label>
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <Input
                            data-testid="auth-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="tu@email.com"
                            className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-11"
                        />
                        <Label className="overline text-muted-foreground">Email</Label>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Input
                            data-testid="auth-password"
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="rounded-none border border-border bg-white focus-visible:ring-0 focus-visible:border-foreground h-11"
                        />
                        <Label className="overline text-muted-foreground">Contraseña</Label>
                    </div>
                    <Button data-testid="auth-submit" disabled={loading} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/85 h-11">
                        {loading ? "Procesando…" : (mode === "login" ? "Ingresar" : "Crear cuenta")}
                    </Button>
                    <div className="text-xs text-muted-foreground text-center">
                        <Link to="/reset-password" className="underline-editorial" data-testid="link-forgot-password">¿Olvidaste tu contraseña?</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
